<script>
  import Layout from '../components/Layout.svelte';
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { getJobValueFromLineItems, getPOValueFromLineItems } from '../lib/billMatching.js';
  import { jobIdentitySlot } from '../lib/trackingSlots.js';
  import { createCurrencyFormatter } from '../lib/currency.js';
  import { user } from '../stores/authStore';

  let bills = [];
  let allBills = [];
  let filteredBills = [];
  let loading = true;
  let error = null;
  let selectedBillIDs = new Set();
  let hideSynced = true; // Default: hide synced bills
  let showPaid = false; // Default: show all invoices (toggle to filter by issue date)
  let paidMonth = ""; // Optional YYYY-MM value for issue date filtering
  
  // Pagination state
  let currentPage = 1;
  let pageSize = 10;
  let totalItems = 0;
  let totalPages = 0;
  
  // Sync status checking state
  let checkingSyncStatus = new Set(); // Set of bill IDs currently being checked
  let syncStatusChecked = new Set(); // Set of bill IDs that have been checked
  let billSyncData = new Map(); // Store sync status data by bill ID
  let selectedMonth = ""; // "01".."12"
  let selectedYear = ""; // yyyy (will be set to number currentYear)
  const monthOptions = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" }
  ];
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];
  // Default the year selector to the current year (number)
  selectedYear = currentYear;
  let searchQuery = ""; // Search filter
  let paidMonthInput; // ref for month input (click input to open native picker)
  
  // Modal state for mark as synced confirmation
  let showMarkSyncedModal = false;
  let pendingMarkSyncedIds = [];

  // Owner's bill tracking settings. The column shows the job-identity slot.
  let billTrackingSettings = null;
  $: userTrackingCategoryName = jobIdentitySlot(billTrackingSettings, 'jtBillNameField')?.xeroCategoryName || 'Job Name';

  function getJobNameFromTracking(bill) {
    return getJobValueFromLineItems(bill?.lineItems, billTrackingSettings, 'jtBillNameField') || '';
  }

  function getJobtreadPOFromTracking(bill) {
    return getPOValueFromLineItems(bill?.lineItems) || '';
  }

  async function fetchBills() {
    try {
      loading = true;
      
      // Only fetch from server if we don't have bills yet, or filters changed
      if (allBills.length === 0 || hasFiltersChanged()) {
        const params = new URLSearchParams();
        const dateFilter = paidMonth && /^\d{4}-\d{2}$/.test(paidMonth);
        // Set filterByDate=true whenever showPaid is enabled, regardless of month selection
        params.set('filterByDate', showPaid ? 'true' : 'false');
        // Only include issueMonth when we have a valid month selected
        if (showPaid && dateFilter) {
          params.set('issueMonth', paidMonth);
        }
        
        // Request all invoices for client-side pagination
        params.set('fetchAll', 'true');
        
        console.log('🚀 Fetching invoices with params:', params.toString(), {
          showPaid,
          issueMonth: paidMonth,
          dateFilter
        });
        
        const response = await fetch(`/api/xero/invoices?${params.toString()}`);
        const data = await response.json();

        console.log('📨 Response from server:', data); 
        if (response.ok) {
          if (data.bills && Array.isArray(data.bills)) {
            console.log('✅ Successfully fetched fresh invoice data from server');
            
            // Store all bills and restore sync status if available
            // Deduplicate bills by invoiceID, keeping the most recent status (PAID over AUTHORISED)
            const billsMap = new Map();
            data.bills.forEach(bill => {
              const existingBill = billsMap.get(bill.invoiceID);
              if (!existingBill || (bill.status === 'PAID' && existingBill.status === 'AUTHORISED')) {
                billsMap.set(bill.invoiceID, bill);
              }
            });
            
            allBills = Array.from(billsMap.values()).map(bill => {
              const savedSyncData = billSyncData.get(bill.invoiceID);
              return {
                ...bill,
                isSynced: savedSyncData?.isSynced || false,
                syncDetails: savedSyncData?.syncDetails || null,
                syncStatusChecked: savedSyncData?.syncStatusChecked || false
              };
            });
            
            // Debug: Check line items structure
            if (allBills.length > 0) {
              const firstBill = allBills[0];
              console.log('First bill structure:', {
                invoiceID: firstBill.invoiceID,
                invoiceNumber: firstBill.invoiceNumber,
                hasLineItems: !!firstBill.lineItems,
                lineItemsCount: firstBill.lineItems?.length || 0,
                allKeys: Object.keys(firstBill)
              });
              if (firstBill.lineItems && firstBill.lineItems.length > 0) {
                console.log('First line item structure:', Object.keys(firstBill.lineItems[0]));
              }
            }
            
            // Calculate pagination metadata
            totalItems = allBills.length;
            totalPages = Math.ceil(totalItems / pageSize);
          } else {
            console.error('Received data is not an array:', data);
            throw new Error('Invalid data format received from server');
          }
        } else if (response.status === 401 && data.needsAuth) {
          console.log('Not authenticated, initiating Xero auth');
          await initiateXeroAuth();
          return;
        } else {
          throw new Error(data.error || 'Failed to fetch bills');
        }
      }
      
      // Apply client-side pagination
      applyClientSidePagination();
      
    } catch (err) {
      console.error('Error fetching bills:', err);
      error = err.message;
    } finally {
      loading = false;
    }
  }
  
  // Track the last fetched filter state
  let lastFetchState = {
    showPaid: false,
    paidMonth: ""
  };

  function hasFiltersChanged() {
    // Check if filter parameters have changed since last fetch
    const currentState = {
      showPaid,
      paidMonth
    };
    
    const changed = (
      currentState.showPaid !== lastFetchState.showPaid ||
      currentState.paidMonth !== lastFetchState.paidMonth
    );
    
    if (changed) {
      console.log('🔄 Filters changed:', { 
        from: lastFetchState, 
        to: currentState 
      });
      // Update the last fetch state
      lastFetchState = { ...currentState };
    }
    
    return changed;
  }
  
  function applyClientSidePagination() {
    // First apply search filter to all bills if search query is valid (2+ chars)
    let filteredAllBills = [...allBills];
    if (searchQuery && searchQuery.trim().length >= 2) {
      const query = searchQuery.toLowerCase().trim();
      filteredAllBills = allBills.filter(bill => 
        (bill.invoiceNumber && bill.invoiceNumber.toLowerCase().includes(query)) ||
        (bill.contact?.name && bill.contact.name.toLowerCase().includes(query))
      );
    }
    
    // Apply "hide synced" filter if enabled
    if (hideSynced) {
      filteredAllBills = filteredAllBills.filter(bill => !bill.isSynced);
    }
    
    // Update total items and pages based on filtered results
    totalItems = filteredAllBills.length;
    totalPages = Math.ceil(totalItems / pageSize);
    
    // Ensure current page is valid for new total
    if (currentPage > totalPages) {
      currentPage = totalPages || 1;
    }
    
    console.log('Applying client-side pagination:', { 
      currentPage, 
      pageSize, 
      totalItems,
      filteredCount: filteredAllBills.length,
      originalCount: allBills.length 
    });
    
    // Calculate end index based on current page (show from start to current page end)
    const endIndex = currentPage * pageSize;
    
    // Get bills from start up to current page end (accumulating pages)
    bills = filteredAllBills.slice(0, endIndex);
    console.log(`Showing ${bills.length} bills (loaded ${currentPage} of ${totalPages} pages)`);
    
    // Set up filteredBills for display (this now represents accumulated results)
    filteredBills = [...bills];
    
    // Start checking sync status for visible bills only
    checkSyncStatusForVisibleBills();
  }

  async function checkSyncStatus(bill) {
    // Skip if currently checking to avoid duplicate requests
    if (checkingSyncStatus.has(bill.invoiceID)) {
      return false;
    }
    
    // If already checked, just return success without making another API call
    if (syncStatusChecked.has(bill.invoiceID)) {
      return true;
    }
    
    checkingSyncStatus.add(bill.invoiceID);
    bills = [...bills];
    applyFilters(false);
    
    try {
        // Use new combined endpoint (checks sync-tracking first, then history)
        const response = await fetch(`/api/xero/check-sync-status/${bill.invoiceID}`);
        const data = await response.json();
        
        // Handle rate limit with exponential backoff
        if (response.status === 429) {
          const retryAfter = response.headers.get('retry-after') || 60;
          console.log(`Rate limited. Waiting ${retryAfter}s before retrying ${bill.invoiceNumber}`);
          await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
          return false;
        }
        
        if (response.ok) {
          console.log(`✅ Bill ${bill.invoiceNumber}: ${data.isSynced ? 'SYNCED' : 'NOT SYNCED'} (source: ${data.source})`);
          
          const syncData = {
            isSynced: data.isSynced,
            syncDetails: data.syncDetails || null,
            syncStatusChecked: true
          };
          
          // Store sync data for later restoration
          billSyncData.set(bill.invoiceID, syncData);
          
          // Update in allBills (the source of truth for pagination)
          const allBillsIndex = allBills.findIndex(b => b.invoiceID === bill.invoiceID);
          if (allBillsIndex !== -1) {
            allBills[allBillsIndex] = {
              ...allBills[allBillsIndex],
              ...syncData
            };
          }
          
          // Update in bills (currently displayed items)
          const billIndex = bills.findIndex(b => b.invoiceID === bill.invoiceID);
          if (billIndex !== -1) {
            bills[billIndex] = {
              ...bills[billIndex],
              ...syncData
            };
            bills = [...bills];
            applyFilters(false);
          }
          return true;
        }
        return false;
      } catch (error) {
        console.error(`Error checking sync status for bill ${bill.invoiceID}:`, error);
        return false;
      } finally {
        checkingSyncStatus.delete(bill.invoiceID);
        syncStatusChecked.add(bill.invoiceID);
        bills = [...bills];
        applyFilters(false);
      }
  }

  async function checkSyncStatusForVisibleBills() {
    console.log('Starting sync status check for', bills.length, 'bills');
    const BATCH_SIZE = 3; // Process 3 bills in parallel
    const MAX_RETRIES = 3;
    let retryQueue = [];
    
    // Reset sync status for all bills that haven't been checked yet
    bills.forEach(bill => {
      if (!syncStatusChecked.has(bill.invoiceID)) {
        bill.isSynced = false;
        bill.syncDetails = null;
        bill.syncStatusChecked = false;
      }
    });
    bills = [...bills]; // Trigger reactivity to show initial state
    applyFilters(false); // Update filteredBills to show initial state (preserve selection)
    
    // First pass: process bills in parallel batches
    for (let i = 0; i < bills.length; i += BATCH_SIZE) {
      const batch = bills.slice(i, i + BATCH_SIZE);
      const results = await Promise.all(
        batch.map(async bill => {
          const success = await checkSyncStatus(bill);
          if (!success) retryQueue.push(bill);
          return success;
        })
      );
      
      // Add a small delay between batches
      if (i + BATCH_SIZE < bills.length) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    
    // Retry failed requests with increasing delays
    let retryAttempt = 0;
    while (retryQueue.length > 0 && retryAttempt < MAX_RETRIES) {
      console.log(`Retry attempt ${retryAttempt + 1}/${MAX_RETRIES} for ${retryQueue.length} bills`);
      const delay = Math.pow(2, retryAttempt) * 1000; // Exponential backoff
      await new Promise(resolve => setTimeout(resolve, delay));
      
      const currentBatch = [...retryQueue];
      retryQueue = [];
      
      // Process retry batch in parallel
      const results = await Promise.all(
        currentBatch.map(async bill => {
          const success = await checkSyncStatus(bill);
          if (!success) retryQueue.push(bill);
          return success;
        })
      );
      
      retryAttempt++;
    }
    
    // Mark any remaining bills as checked to prevent infinite retries
    if (retryQueue.length > 0) {
      console.warn(`${retryQueue.length} bills failed to check sync status after ${MAX_RETRIES} retries`);
      retryQueue.forEach(bill => {
        syncStatusChecked.add(bill.invoiceID);
        
        // Update in allBills (the source of truth for pagination)
        const allBillsIndex = allBills.findIndex(b => b.invoiceID === bill.invoiceID);
        if (allBillsIndex !== -1) {
          allBills[allBillsIndex] = {
            ...allBills[allBillsIndex],
            syncStatusChecked: true,
            syncError: true
          };
        }
        
        // Update in bills (currently displayed items)
        const billIndex = bills.findIndex(b => b.invoiceID === bill.invoiceID);
        if (billIndex !== -1) {
          bills[billIndex] = {
            ...bills[billIndex],
            syncStatusChecked: true,
            syncError: true
          };
        }
      });
      bills = [...bills]; // Trigger reactivity
      applyFilters(false); // Update filteredBills (preserve selection during retry)
    }
    
    // After all sync checks complete, auto-load more pages if all current bills are hidden
    await autoLoadMoreIfAllHidden();
  }
  
  async function autoLoadMoreIfAllHidden() {
    // Only auto-load if hideSynced is enabled and there are more pages
    if (!hideSynced || currentPage >= totalPages) {
      return;
    }
    
    // Check if all currently loaded bills are hidden (synced)
    // Only count bills that have been checked
    const checkedBills = bills.filter(bill => syncStatusChecked.has(bill.invoiceID));
    const visibleBills = checkedBills.filter(bill => !bill.isSynced);
    
    console.log(`Auto-load check: ${visibleBills.length} visible bills out of ${checkedBills.length} checked bills`);
    
    // If we have no visible bills but more pages exist, load the next page
    if (visibleBills.length === 0 && checkedBills.length > 0 && currentPage < totalPages) {
      console.log('All current bills are hidden and more pages exist. Auto-loading next page...');
      currentPage++;
      applyClientSidePagination();
      // The applyClientSidePagination will trigger checkSyncStatusForVisibleBills again
      // which will recursively call this function until we have visible bills or run out of pages
    }
  }
  function applyFilters(resetSelection = true) {
    // For server-side pagination, we only apply client-side filters that don't affect the total count
    // The main type/status filtering is handled by the server
    
    let result = [...bills]; // Start with the current page of bills
    
    // Apply "hide synced" filter if enabled (client-side filter)
    if (hideSynced) {
      result = result.filter(bill => !bill.isSynced);
    }

    // Apply search filter if search query exists (client-side filter)
    if (searchQuery && searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(bill => 
        (bill.invoiceNumber && bill.invoiceNumber.toLowerCase().includes(query)) ||
        (bill.contact?.name && bill.contact.name.toLowerCase().includes(query))
      );
    }

    filteredBills = result;
    
    // Don't overwrite bills array for pagination - just use filteredBills for display
    // bills = filteredBills; // Remove this line to preserve pagination

    // Only reset selection when explicitly requested (filter changes, not sync updates)
    if (resetSelection) {
      selectedBillIDs = new Set();
    }
  }

  function toggleHideSynced() {
    hideSynced = !hideSynced;
    // Don't reset currentPage - keep showing all loaded items with filter applied
    applyClientSidePagination();
  }

  function toggleShowPaid() {
    showPaid = !showPaid;
    currentPage = 1; // Reset to first page when filter changes
    if (!showPaid) {
      // Turning off: restore default AUTHORISED view
      paidMonth = "";
      selectedMonth = "";
      selectedYear = currentYear;
      loading = true;
      fetchBills().finally(() => {
        loading = false;
      });
    } else {
      // Turning on: trigger 60-day paid/authorized view immediately
      paidMonth = "";
      selectedMonth = "";
      selectedYear = currentYear;
      loading = true;
      fetchBills().finally(() => {
        loading = false;
      });
    }
  }

  async function loadPaidMonth() {
    if (!showPaid) return;
    // Build YYYY-MM from dropdowns
    if (selectedYear && selectedMonth) {
      paidMonth = `${selectedYear}-${selectedMonth}`;
      console.log(`🔍 Loading invoices issued in: ${paidMonth} (Date filter)`);
    } else {
      return;
    }
    currentPage = 1; // Reset to first page when loading new data
    loading = true;
    await fetchBills();
    loading = false;
  }

  function handleSearch(event) {
    searchQuery = event.target.value;
    // Don't reset currentPage - keep showing all loaded items with search applied
    applyClientSidePagination();
  }

  function handleRetry() {
    // Force a full page reload to ensure a fresh state and bypass any client cache
    window.location.reload();
  }

  async function initiateXeroAuth() {
    try {
      const response = await fetch('/api/xero/check-auth');
      const data = await response.json();
      if (data.redirectUrl) {
        window.location.href = data.redirectUrl;
      } else {
        throw new Error('Failed to get Xero authentication URL');
      }
    } catch (err) {
      console.error('Error initiating Xero auth:', err);
      error = 'Failed to initiate Xero authentication';
      loading = false;
    }
  }

  onMount(async () => {
    // Fetch user settings for custom tracking category
    try {
      const userResponse = await fetch('/api/user');
      if (userResponse.ok) {
        const userData = await userResponse.json();
        if (userData.success && userData.user?.billSettings) {
          billTrackingSettings = userData.user.billSettings;
        }
      }
    } catch (error) {
      console.error('Error fetching user settings:', error);
    }
    
    // Fetch bills
    await fetchBills();
  });

  function formatDate(dateString) {
    return new Date(dateString).toLocaleDateString();
  }

  $: formatCurrency = createCurrencyFormatter($user?.country);

  async function toggleBillSelection(billID) {
    console.log('Toggling bill:', billID);
    if (billID === undefined) {
      console.error('Received undefined billID');
      return;
    }
    selectedBillIDs = new Set(selectedBillIDs);
    if (selectedBillIDs.has(billID)) {
      selectedBillIDs.delete(billID);
    } else {
      selectedBillIDs.add(billID);
    }
    console.log('Selected bill IDs after toggle:', Array.from(selectedBillIDs));
  }

  function handleRowClick(bill) {
    if (bill.isSynced) return;
    toggleBillSelection(bill.invoiceID);
  }

  async function processSelected() {
    console.log('Processing selected bills. Current selection:', Array.from(selectedBillIDs));
    const selectedBillsData = filteredBills.filter(bill => selectedBillIDs.has(bill.invoiceID));
    
    console.log('Filtered selected bills:', JSON.stringify(selectedBillsData, null, 2));
    if (selectedBillsData.length > 0) {
      loading = true;

      // Clear the invoice history cache for selected bills
      try {
        const response = await fetch('/api/xero/clear-selected-bills-cache', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            invoiceIds: selectedBillsData.map(bill => bill.invoiceID)
          })
        });

        if (!response.ok) {
          const error = await response.json();
          console.error('Failed to clear invoice history cache:', error);
          // Non-critical error, continue with processing
        } else {
          console.log('Successfully cleared invoice history cache for selected bills');
        }
      } catch (error) {
        console.error('Error clearing invoice history cache:', error);
        // Non-critical error, continue with processing
      }
      
      try {
        // Pass bills through - matching happens on sync page from line items
        navigate('/sync-selected-bills-from-xero', { state: { selectedBills: selectedBillsData, syncMode: 'manual' } });
      } catch (error) {
        console.error('Error checking JobTread matches:', error);
      } finally {
        loading = false;
      }
    } else {
      console.log('No bills selected');
      // Optionally, show an alert or message to the user
    }
  }

  $: canProcessSelected = selectedBillIDs.size > 0;
  $: canMarkSelected = selectedBillIDs.size > 0;
  $: console.log('Can process selected:', canProcessSelected, 'Selected count:', selectedBillIDs.size); // Debug log
  $: console.log('First bill:', bills[0]);
  
  // Select all functionality
  $: selectableFilteredBills = filteredBills.filter(bill => !bill.isSynced);
  $: allSelectableAreSelected = selectableFilteredBills.length > 0 && 
    selectableFilteredBills.every(bill => selectedBillIDs.has(bill.invoiceID));
  $: someSelected = selectableFilteredBills.some(bill => selectedBillIDs.has(bill.invoiceID));

  function markSelectedAsSynced() {
    const selectedBillsArray = Array.from(selectedBillIDs);
    
    if (selectedBillsArray.length === 0) {
      console.log('No bills selected for manual sync marking');
      return;
    }

    // Show confirmation modal
    pendingMarkSyncedIds = selectedBillsArray;
    showMarkSyncedModal = true;
  }

  async function confirmMarkAsSynced() {
    showMarkSyncedModal = false;
    
    if (pendingMarkSyncedIds.length === 0) {
      return;
    }

    try {
      console.log(`Marking ${pendingMarkSyncedIds.length} Xero bills as synced...`);
      
      const response = await fetch('/api/xero/mark-bills-as-synced', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoiceIds: pendingMarkSyncedIds })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        console.log(`✅ Successfully marked ${result.successCount} bills as synced`);
        
        // Register in syncStatusChecked Set so checkSyncStatusForVisibleBills
        // does not immediately reset these bills back to isSynced=false
        pendingMarkSyncedIds.forEach(id => syncStatusChecked.add(id));
        
        // Update allBills array to mark selected bills as synced
        allBills = allBills.map(bill => {
          if (selectedBillIDs.has(bill.invoiceID)) {
            const syncData = {
              isSynced: true,
              syncStatusChecked: true,
              syncDetails: null
            };
            
            // Also update the billSyncData Map for persistence
            billSyncData.set(bill.invoiceID, syncData);
            
            return {
              ...bill,
              ...syncData
            };
          }
          return bill;
        });
        
        // Clear selection
        selectedBillIDs = new Set();
        
        // Refresh the view with pagination
        applyClientSidePagination();
      } else {
        console.error('❌ Failed to mark bills as synced:', result.error);
        alert(`Failed to mark bills as synced: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Error marking bills as synced:', error);
      alert(`Error marking bills as synced: ${error.message}`);
    } finally {
      pendingMarkSyncedIds = [];
    }
  }

  function cancelMarkAsSynced() {
    showMarkSyncedModal = false;
    pendingMarkSyncedIds = [];
  }

  function handleModalBackdropClick(e) {
    if (e.target === e.currentTarget) {
      cancelMarkAsSynced();
    }
  }

  function toggleSelectAll() {
    if (allSelectableAreSelected) {
      // Deselect all
      selectableFilteredBills.forEach(bill => {
        selectedBillIDs.delete(bill.invoiceID);
      });
    } else {
      // Select all selectable bills
      selectableFilteredBills.forEach(bill => {
        selectedBillIDs.add(bill.invoiceID);
      });
    }
    selectedBillIDs = new Set(selectedBillIDs);
  }
</script>

<Layout activeRoute="import-bills-from-xero">
  <div class="container mx-auto px-4 py-8">
    <h1 class="text-2xl font-bold mb-6">Import Vendor Bills from Xero to JobTread</h1>
    <p class="text-sm text-gray-600 mb-4">Showing bills with status: Approved</p>
    {#if loading}
      <div class="flex justify-center items-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    {:else if error}
      <div class="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded flex items-center justify-between">
        <span class="text-sm">{error}</span>
        <button
          on:click={handleRetry}
          class="ml-4 bg-blue-600 text-white text-sm px-3 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          Retry
        </button>
      </div>
    {:else if allBills.length === 0}
      <div class="flex items-center justify-between bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded">
        <p class="text-sm">No bills in Xero found matching the criteria (ACCPAY/ACCREC and Authorised/Paid) - Ensure your Xero Bills are approved perhaps they are draft?</p>
        <button
          on:click={handleRetry}
          class="ml-4 bg-blue-600 text-white text-sm px-3 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          Refresh
        </button>
      </div>
      
    {:else}
      <div class="bg-white rounded-lg shadow overflow-hidden">
        <div class="p-4 border-b">
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <label class="inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  class="sr-only peer focus:outline-none focus-visible:outline-none focus:ring-0" 
                  checked={hideSynced}
                  on:change={toggleHideSynced}
                >
                <div class="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                <span class="ms-3 text-sm font-medium text-gray-900">
                  {hideSynced ? 'Hide Synced' : 'Hide Synced'}
                </span>
              </label>
              <label class="inline-flex items-center cursor-pointer ml-6">
                <input 
                  type="checkbox" 
                  class="sr-only peer focus:outline-none focus-visible:outline-none focus:ring-0" 
                  checked={showPaid}
                  on:change={toggleShowPaid}
                >
                <div class="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                <span class="ms-3 text-sm font-medium text-gray-900">Show Paid</span>
                {#if showPaid}
                  <div class="ml-3 flex items-center gap-2">
                    <label class="text-xs text-gray-600" for="paidMonthSelect">Issued within Month:</label>
                    <select
                      id="paidMonthSelect"
                      class="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
                      bind:value={selectedMonth}
                    >
                      <option value="" disabled>Select month</option>
                      {#each monthOptions as m}
                        <option value={m.value}>{m.label}</option>
                      {/each}
                    </select>
                    <label class="text-xs text-gray-600" for="paidYearSelect">Year:</label>
                    <select
                      id="paidYearSelect"
                      class="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
                      bind:value={selectedYear}
                    >
                      <option value="" disabled>Select year</option>
                      {#each yearOptions as y}
                        <option value={y}>{y}</option>
                      {/each}
                    </select>
                    <button
                      class="bg-blue-600 text-white text-xs px-2 py-1 rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      on:click={loadPaidMonth}
                      disabled={!selectedMonth || !selectedYear}
                    >
                      Load
                    </button>
                  </div>
                {/if}
              </label>
            </div>
            <div class="flex flex-wrap gap-2 items-center">
              <button 
                on:click={markSelectedAsSynced} 
                disabled={!canMarkSelected} 
                class="inline-flex items-center justify-center h-9 px-4 text-sm font-medium rounded-md whitespace-nowrap transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400 bg-white text-gray-600 border border-gray-300 hover:bg-[#f0f4ff] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Mark as Synced ({selectedBillIDs.size})
              </button>
              <button 
                on:click={processSelected} 
                disabled={!canProcessSelected} 
                class="inline-flex items-center justify-center h-9 px-4 text-sm font-medium rounded-md whitespace-nowrap transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Process Selected ({selectedBillIDs.size})
              </button>
            </div>
          </div>
        </div>

        <!-- Search Filter Section -->
        <div class="p-4 pb-8 border-b bg-gray-50">
          <div class="relative">
            <div class="relative">
              <!-- Magnifying glass icon -->
              <svg class="absolute left-3 top-2.5 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search bills by reference or vendor name (min. 2 chars)..."
                class="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm {searchQuery && searchQuery.trim().length === 1 ? 'border-yellow-400' : ''}"
                value={searchQuery}
                on:input={handleSearch}
              />
              {#if searchQuery && searchQuery.trim().length === 1}
                <div class="absolute -bottom-6 left-0 text-xs text-yellow-600">
                  Please enter at least 2 characters to search
                </div>
              {/if}
            </div>
            {#if searchQuery}
              <button
                class="absolute right-3 top-2 text-gray-400 hover:text-gray-600"
                on:click={() => {
                  searchQuery = "";
                  // Don't reset currentPage - keep showing all loaded items
                  applyClientSidePagination();
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
              </button>
            {/if}
          </div>
        </div>

        <!-- Status Info -->
        <div class="flex items-center justify-center px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
          <p class="text-sm text-gray-700">
            Showing
            <span class="font-medium">{bills.length}</span>
            of
            <span class="font-medium">{totalItems}</span>
            results
          </p>
        </div>

        <!-- Select All -->
        <div class="px-4 py-3 bg-white border-b border-gray-200">
          <div class="flex items-center">
            <input
              type="checkbox"
              class="form-checkbox h-5 w-5 text-primary"
              checked={allSelectableAreSelected}
              on:change={toggleSelectAll}
              disabled={selectableFilteredBills.length === 0}
            >
            <span class="ml-2 text-gray-700">Select All</span>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full table-fixed">
            <thead class="bg-white">
              <tr>
                <th class="w-[4%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Select</th>
                <th class="w-[9%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Bill Reference</th>
                <th class="w-[13%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Vendor</th>
                <th class="w-[8%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Job Name</th>
                <th class="w-[7%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">JT PO</th>
                <th class="w-[8%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">{userTrackingCategoryName}</th>
                <th class="w-[9%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Date</th>
                <th class="w-[9%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Due Date</th>
                <th class="w-[9%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Total</th>
                <th class="w-[10%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Status</th>
                <th class="w-[14%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Sync Status</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-[#edf0f7]">
              {#if filteredBills.length === 0}
                <tr>
                  <td colspan="11" class="px-6 py-8 text-center text-gray-500">
                    <div class="flex flex-col items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <p class="text-lg font-medium text-gray-900 mb-1">No results found</p>
                      <p class="text-sm text-gray-500">
                        {#if searchQuery}
                          No bills match your search for "{searchQuery}". Try adjusting your search terms.
                        {:else}
                          No bills match the current filters.
                        {/if}
                      </p>
                    </div>
                  </td>
                </tr>
              {:else}
                {#each filteredBills as bill (bill.invoiceID)}
                <tr class="hover:bg-[#f0f4ff] {bill.isSynced ? '' : 'cursor-pointer'}" on:click={() => handleRowClick(bill)}>
                  <td class="px-2 py-3 whitespace-nowrap text-sm" on:click={(e) => e.stopPropagation()}>
                    <input 
                      type="checkbox" 
                      class="form-checkbox h-4 w-4 text-primary"
                      checked={selectedBillIDs.has(bill.invoiceID)}
                      on:change={() => toggleBillSelection(bill.invoiceID)}
                      disabled={bill.isSynced}
                    />
                  </td>
                  <td class="px-2 py-3 whitespace-nowrap text-sm truncate">{bill.invoiceNumber || 'N/A'}</td>
                  <td class="px-2 py-3 whitespace-nowrap text-sm truncate">{bill.contact?.name || 'N/A'}</td>
                  <td class="px-2 py-3 whitespace-nowrap text-sm truncate">{getJobNameFromTracking(bill)}</td>
                  <td class="px-2 py-3 whitespace-nowrap text-sm truncate">{getJobtreadPOFromTracking(bill)}</td>
                  <td class="px-2 py-3 whitespace-nowrap text-sm truncate">{getJobNameFromTracking(bill)}</td>
                  <td class="px-2 py-3 whitespace-nowrap text-sm">{formatDate(bill.date)}</td>
                  <td class="px-2 py-3 whitespace-nowrap text-sm">{formatDate(bill.dueDate)}</td>
                  <td class="px-2 py-3 whitespace-nowrap text-sm">{formatCurrency(bill.total)}</td>
                  <td class="px-2 py-3 whitespace-nowrap text-sm">
                    <span class="px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full {bill.status === 'AUTHORISED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}">
                      {bill.status}
                    </span>
                  </td>
                  <td class="px-2 py-3 whitespace-nowrap text-sm">
                    {#if checkingSyncStatus.has(bill.invoiceID)}
                      <span class="px-1.5 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Checking...
                      </span>
                    {:else if syncStatusChecked.has(bill.invoiceID)}
                      {#if bill.isSynced}
                        <span class="px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Synced to JT
                        </span>
                      {:else}
                        <span class="px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          Needs Sync
                        </span>
                      {/if}
                    {:else}
                      <span class="px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                        Pending Check
                      </span>
                    {/if}
                  </td>
                </tr>
              {/each}
              {/if}
            </tbody>
          </table>
        </div>

        <!-- Show More Button -->
        {#if currentPage < totalPages}
          <div class="flex items-center justify-center px-4 py-4 bg-white border-t border-gray-200">
            <button
              on:click={() => {
                currentPage++;
                applyClientSidePagination();
              }}
              disabled={loading}
              class="relative inline-flex items-center px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {#if loading}
                <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading...
              {:else}
                Show More ({Math.min(pageSize, totalItems - bills.length)} more)
              {/if}
            </button>
          </div>
        {/if}
      </div>
    {/if}
  </div>

  <!-- Mark as Synced Confirmation Modal -->
  {#if showMarkSyncedModal}
    <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" on:click={handleModalBackdropClick} on:keydown={(e) => e.key === 'Escape' && cancelMarkAsSynced()} role="button" tabindex="-1">
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" role="dialog" aria-modal="true">
        <h3 class="text-lg font-semibold text-gray-900 mb-3">Mark as Synced</h3>
        <p class="text-gray-600 mb-4">
          Are you sure you want to mark <span class="font-semibold">{pendingMarkSyncedIds.length} bill{pendingMarkSyncedIds.length > 1 ? 's' : ''}</span> as synced?
        </p>
        <p class="text-sm text-gray-500 mb-6">
          This will prevent them from appearing as "needing sync" in the future.
        </p>
        <div class="flex gap-3 justify-end">
          <button
            on:click={cancelMarkAsSynced}
            class="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            on:click={confirmMarkAsSynced}
            class="px-4 py-2 text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
          >
            Mark as Synced
          </button>
        </div>
      </div>
    </div>
  {/if}
</Layout>

<style>
  .process-invoices {
    padding: 16px;
  }
  .invoice-table-container {
    margin-bottom: 16px;
  }
  
  /* Make table responsive */
  @media (max-width: 1200px) {
    .table-fixed {
      table-layout: auto;
    }
    
    td {
      white-space: normal;
      word-break: break-word;
      font-size: 0.75rem;
      padding: 0.5rem 0.25rem;
    }
    
    th {
      white-space: nowrap !important;
      font-size: 0.75rem !important;
      padding: 0.5rem 0.25rem;
    }
  }
  /* Hide the native calendar icon on WebKit/Blink */
  input.month-input-custom::-webkit-calendar-picker-indicator {
    opacity: 0;
    -webkit-appearance: none;
    appearance: none;
    pointer-events: none; /* ensure clicks go to our left icon */
  }
  /* Hide the clear button in Edge/IE (defensive) */
  input.month-input-custom::-ms-clear { display: none; }
  input.month-input-custom::-ms-reveal { display: none; }
</style>